import { useState, useEffect, useRef, useCallback } from "react";
import type { StateMap } from "../types";
import type { HiddenMap } from "../lib/hiddenFixes";
import { AuditSection } from "./AuditSection";
import { showAlert, showConfirm } from "../lib/dialog";
import { wireCardTrash, TRASH_SVG } from "../lib/cardTrash";
import { confirmDeletePopover } from "../lib/confirmPopover";
import { loadAnthropicKey } from "./SettingsModal";
import { scheduleFlush } from "../lib/siteData";

interface Props {
  state: StateMap;
  siteBrand?: string;
  auditDate?: string;
  onApprove: (id: string, message?: string, isCreate?: boolean) => void;
  onIgnore: (id: string, checked: boolean) => void;
  onDone: (id: string, checked: boolean) => void;
  onReset: (id: string) => void;
  hidden: HiddenMap;
  onToggleHide: (id: string) => void;
  onClearFixIds?: (ids: string[]) => void; // wipe stale status/hidden for reused fix-ids on recrawl
  connected: boolean;
  fetchPageTags: (pathSlug: string) => Promise<string[] | null>;
  onOpenSchemaSetup?: () => void;
}

const normalizeUrl = (u: string): string => u.trim().toLowerCase().replace(/\/+$/, "");

// Targets that already have cards (read from each card's data-il-to).
function extractCardedTargets(html: string): Set<string> {
  const set = new Set<string>();
  const re = /data-il-to="([^"]+)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) set.add(normalizeUrl(m[1].replace(/&amp;/g, "&")));
  return set;
}
// Next free global card index so appended cards don't collide with existing il-bulk-* ids.
function nextCardIndex(html: string): number {
  let max = -1;
  const re = /id="oc-il-bulk-\d+-(\d+)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) { const n = parseInt(m[1], 10); if (n > max) max = n; }
  return max + 1;
}

type RawMeta = { url?: string; h1?: string; title?: string; excerpt?: string; wordCount?: number; bodyLinks?: string[]; error?: string };

// The crawled page inventory, from EITHER source (standalone "Crawl Website" is preferred, then the
// full-audit / Get Competitors crawl). Returns [] when neither has been run.
function rawPageMeta(): RawMeta[] {
  // 1) Standalone site crawl (the dedicated "Crawl Website" button — no competitors needed).
  try {
    const s = JSON.parse(localStorage.getItem("seo_bil_pages") ?? "null") as { pageMeta?: RawMeta[] } | null;
    if (Array.isArray(s?.pageMeta) && s.pageMeta.length) return s.pageMeta;
  } catch { /* ignore */ }
  // 2) Fall back to the audit crawl (Get Competitors / Run Full Audit) if present.
  try {
    const fm = JSON.parse(localStorage.getItem("seo_full_metrics") ?? "null") as { pageMeta?: RawMeta[] } | null;
    if (Array.isArray(fm?.pageMeta)) return fm.pageMeta;
  } catch { /* ignore */ }
  return [];
}

// When (if ever) the standalone crawl last ran — for the "Last crawled" status. null if never.
function lastSiteCrawlAt(): string | null {
  try { const s = JSON.parse(localStorage.getItem("seo_bil_pages") ?? "null") as { crawledAt?: string } | null; return s?.crawledAt ?? null; } catch { return null; }
}

// Build the source-page inventory. Relevance is judged on the on-page PAGE TITLE (the H1), which is
// more descriptive than the meta <title> — fall back to the meta title only when no H1 was captured.
function buildPageInventory(): Array<{ url: string; title: string; excerpt: string; wordCount?: number; bodyLinks?: string[] }> {
  return rawPageMeta()
    .filter(p => p?.url && !p.error)
    .map(p => ({
      url: p.url as string,
      title: ((p.h1 ?? "").trim() || (p.title ?? "").trim()),
      excerpt: (p.excerpt ?? "").slice(0, 1500), // more copy → generator can reuse existing phrases
      wordCount: typeof p.wordCount === "number" ? p.wordCount : undefined,
      bodyLinks: Array.isArray(p.bodyLinks) ? p.bodyLinks : undefined, // skip sources already body-linking a target
    }))
    .slice(0, 500); // send the whole crawled site so every page is a possible source
}

export function BulkInternalLinks(props: Props) {
  // Persisted so the pasted URLs + generated cards survive tab switches.
  const [urls, setUrls] = useState(() => { try { return localStorage.getItem("lsg_bil_urls") ?? ""; } catch { return ""; } });
  const [cardsHtml, setCardsHtml] = useState(() => { try { return localStorage.getItem("lsg_bil_cards") ?? ""; } catch { return ""; } });
  // Max link suggestions per target URL: "5" | "10" | "20" | "30" | "max". Default "10".
  const [maxPerUrl, setMaxPerUrl] = useState(() => { try { return localStorage.getItem("lsg_bil_maxper") ?? "10"; } catch { return "10"; } });
  const [building, setBuilding] = useState(false);
  const [status, setStatus] = useState("");
  // Dedicated "Crawl Website" step (reads all the site's pages so the tool has a source inventory
  // WITHOUT needing Get Competitors). `crawledAt` drives the "Last crawled" status.
  const [crawling, setCrawling] = useState(false);
  const [crawlStatus, setCrawlStatus] = useState("");
  const [crawledAt, setCrawledAt] = useState<string | null>(() => lastSiteCrawlAt());
  const cardsRef = useRef<HTMLDivElement | null>(null);

  // lsg_bil_urls + lsg_bil_cards are per-site (AUDIT_KEYS) — write locally AND scheduleFlush so the
  // change (generate, delete a card, remove an emptied group) syncs to the server. Without the flush,
  // a refresh re-hydrates the server's stale copy and deleted cards "come back".
  useEffect(() => { try { localStorage.setItem("lsg_bil_urls", urls); scheduleFlush(); } catch { /* ignore */ } }, [urls]);
  useEffect(() => { try { localStorage.setItem("lsg_bil_cards", cardsHtml); scheduleFlush(); } catch { /* ignore */ } }, [cardsHtml]);
  useEffect(() => { try { localStorage.setItem("lsg_bil_maxper", maxPerUrl); } catch { /* ignore */ } }, [maxPerUrl]);

  // Read the site URL to crawl (the cover "Website URL"). The Express tab doesn't render the cover,
  // so it comes from the persisted cover field.
  const siteUrlForCrawl = (): string => {
    try { return (localStorage.getItem("cover_website_url") ?? "").trim(); } catch { return ""; }
  };

  // Dedicated site crawl: reads all pages on the website so the tool can find link opportunities.
  async function handleCrawlSite() {
    const siteUrl = siteUrlForCrawl();
    if (!siteUrl) {
      void showAlert("Enter your Website URL on the cover page first, then click Crawl Website.", { title: "No Website URL" });
      return;
    }
    const proceed = await showConfirm(
      `This reads every page on ${siteUrl} (title + body text) so the tool can find internal-link opportunities. Large sites can take a few minutes. It does not use competitor data.`,
      { title: "Crawl Website", confirmLabel: "Crawl Website", cancelLabel: "Cancel", tone: "info" },
    );
    if (!proceed) return;
    setCrawling(true);
    setCrawlStatus("Crawling all pages on your website… this can take a few minutes on large sites.");
    try {
      const res = await fetch("/api/crawl-site", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ siteUrl }),
      });
      const data = await res.json() as { ok: boolean; pageMeta?: unknown[]; domain?: string; crawled?: number; error?: string };
      if (data.ok && Array.isArray(data.pageMeta) && data.pageMeta.length) {
        const at = new Date().toISOString();
        try { localStorage.setItem("seo_bil_pages", JSON.stringify({ domain: data.domain ?? "", crawledAt: at, pageMeta: data.pageMeta })); } catch { /* ignore */ }
        setCrawledAt(at);
        setCrawlStatus(`Done — crawled ${data.crawled ?? data.pageMeta.length} page${(data.crawled ?? data.pageMeta.length) === 1 ? "" : "s"}. You can now generate internal-link opportunities below.`);
      } else {
        setCrawlStatus(`⚠ ${data.error ?? "No pages were found to crawl. Check the Website URL on the cover."}`);
      }
    } catch (e) {
      setCrawlStatus(`⚠ ${e instanceof Error ? e.message : "Network error"}`);
    } finally {
      setCrawling(false);
    }
  }

  // Remove one card (trash button) from the stored cards HTML. Cards are grouped per target URL in a
  // `.sub` block whose header shows the opportunity count — so also update that count, and drop the
  // whole group (header included) once its last card is deleted.
  const removeCard = useCallback((cardId: string) => {
    setCardsHtml(prev => {
      const tmp = document.createElement("div");
      tmp.innerHTML = prev;
      const card = tmp.querySelector(`[id="${cardId}"]`);
      const group = card?.closest(".sub") as HTMLElement | null;
      card?.remove();
      if (group) {
        const remaining = group.querySelectorAll(".oc").length;
        if (remaining === 0) {
          group.remove(); // no cards left under this target → remove its header/group entirely
        } else {
          const atag = group.querySelector(".atag");
          if (atag) atag.textContent = `${remaining} ${remaining === 1 ? "opportunity" : "opportunities"}`;
        }
      }
      return tmp.innerHTML.trim();
    });
  }, []);
  // Inject + wire a per-card trash button on each card.
  useEffect(() => { const r = cardsRef.current; if (!r) return; return wireCardTrash(r, removeCard); }, [cardsHtml, removeCard]);

  // Remove an ENTIRE target group (its header + all its opportunity cards), matched by the target URL
  // shown in the group header's .mono. Drops the whole `.sub` block from the stored cards HTML.
  const removeGroup = useCallback((target: string) => {
    setCardsHtml(prev => {
      const tmp = document.createElement("div");
      tmp.innerHTML = prev;
      tmp.querySelectorAll(".sub").forEach(sub => {
        if (sub.querySelector(".sub-lbl .mono")?.textContent?.trim() === target) sub.remove();
      });
      return tmp.innerHTML.trim();
    });
  }, []);
  // Inject + wire a trash button in each target group's header (to the right of the opportunity
  // count) that deletes every card under that target URL, after a confirm.
  useEffect(() => {
    const r = cardsRef.current;
    if (!r) return;
    const cleanups: Array<() => void> = [];
    r.querySelectorAll<HTMLElement>(".sub-lbl").forEach(lbl => {
      const target = lbl.querySelector(".mono")?.textContent?.trim() ?? "";
      if (!target) return;
      let btn = lbl.querySelector<HTMLButtonElement>(".bil-group-trash");
      if (!btn) {
        btn = document.createElement("button");
        btn.type = "button";
        btn.className = "cd-trash-btn bil-group-trash";
        btn.title = "Delete all opportunities for this target URL";
        btn.setAttribute("aria-label", "Delete all opportunities for this target URL");
        btn.style.marginLeft = "8px";
        btn.style.verticalAlign = "middle";
        btn.innerHTML = TRASH_SVG;
        lbl.appendChild(btn); // to the right of the .atag opportunity count
      }
      const trash = btn;
      const h = (e: Event) => {
        e.preventDefault(); e.stopPropagation();
        confirmDeletePopover(
          trash,
          () => removeGroup(target),
          "All internal link opportunity cards for this target URL will be removed from the list if you click Continue.",
          "Continue",
          true, // multi-line message
        );
      };
      trash.addEventListener("click", h);
      cleanups.push(() => trash.removeEventListener("click", h));
    });
    return () => cleanups.forEach(fn => fn());
  }, [cardsHtml, removeGroup]);

  async function handleContinue() {
    const list = urls.split("\n").map(u => u.trim()).filter(Boolean);
    const valid = list.filter(u => /^https?:\/\//i.test(u));
    if (!valid.length) {
      void showAlert("Paste at least one full target URL (starting with https://) — one per line. These are the pages you want OTHER pages on your site to link to.", { title: "No URLs" });
      return;
    }
    // Drop targets that already have cards + duplicates within the box.
    const carded = extractCardedTargets(cardsHtml);
    const seen = new Set<string>();
    const fresh = valid.filter(u => {
      const key = normalizeUrl(u);
      if (carded.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (!fresh.length) {
      void showAlert("Every URL in the box already has internal-link cards. Add one or more new target URLs.", { title: "Nothing new" });
      return;
    }
    const anthropicKey = loadAnthropicKey();
    if (!anthropicKey) {
      void showAlert("Anthropic API key not set.\n\nGo to ⚙ Settings and add your Anthropic API key (sk-ant-…).", { title: "API Key Required" });
      return;
    }
    const pages = buildPageInventory();
    if (!pages.length) {
      void showAlert(
        "This tool needs to read your website's pages before it can find internal-link opportunities.\n\nClick “Crawl Website” at the top of this tab first (it reads all your pages), then try again. (Running “Get Competitors” on the cover also produces this data.)",
        { title: "Run a website crawl first" },
      );
      return;
    }
    const T = fresh.length;
    const subject = T === 1 ? "this 1 target URL" : `these ${T} target URLs`;
    const intro = `The tool will scan your site's crawled pages — using each page's title to judge relevance, then its content to find natural internal-link opportunities pointing to ${subject}. A card is created for each opportunity, grouped by target URL.`;
    // Card-count estimate for the confirmation: opportunities PER url × number of target URLs.
    const n = Number(maxPerUrl);
    const total = n * T;
    const countPlain = maxPerUrl === "max"
      ? `You chose "Max" per URL, so the tool creates a card for every relevant source page it finds for each URL — total = (relevant pages per URL) × ${T} target URL${T === 1 ? "" : "s"}. The exact count depends on how many relevant pages exist.`
      : `At ${maxPerUrl} per URL × ${T} target URL${T === 1 ? "" : "s"}, this can create up to ${total} link card${total === 1 ? "" : "s"} — the actual count depends on how many relevant source pages are found.`;
    const countHtml = maxPerUrl === "max"
      ? `You chose <strong>Max</strong> per URL, so the tool creates a card for <strong>every relevant source page</strong> it finds for each URL — total = (relevant pages per URL) × <strong>${T}</strong> target URL${T === 1 ? "" : "s"}. The exact count depends on how many relevant pages exist.`
      : `At <strong>${maxPerUrl}</strong> per URL × <strong>${T}</strong> target URL${T === 1 ? "" : "s"}, this can create up to <strong>${total}</strong> link card${total === 1 ? "" : "s"} — the actual count depends on how many relevant source pages are found.`;
    const reminder = "Remember: the suggestions per URL are multiplied by the number of target URLs you entered.";
    const proceed = await showConfirm(
      `${intro}\n\n${countPlain}\n\n${reminder}`,
      {
        title: "Find internal-link opportunities",
        confirmLabel: "Continue",
        cancelLabel: "Cancel",
        tone: "info",
        messageHtml: `${intro}<br><br>${countHtml}<br><br>${reminder}`,
      },
    );
    if (!proceed) return;

    setBuilding(true);
    setStatus(`Scanning your pages for links to ${fresh.length} target${fresh.length === 1 ? "" : "s"}… (this can take 15–60 sec)`);
    try {
      const res = await fetch("/api/bulk-internal-links", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          targetUrls: fresh,
          pages,
          startIndex: nextCardIndex(cardsHtml),
          anthropicKey,
          maxPerUrl: maxPerUrl === "max" ? "max" : Number(maxPerUrl),
          brand: (localStorage.getItem("cover_business_name") ?? "").trim() || undefined,
          location: (localStorage.getItem("cover_location") ?? "").trim() || undefined,
        }),
      });
      const data = await res.json() as { ok: boolean; html?: string; error?: string };
      if (data.ok && data.html) {
        // Fix-ids (il-bulk-*) are reused across recrawls, so a new card could inherit a prior batch's
        // queued/done/ignored or hidden status (esp. after clearing). Wipe status for the new fix-ids
        // so every freshly generated card starts blank.
        const newIds: string[] = [];
        const idRe = /id="oc-(il-bulk-\d+-\d+)"/g;
        let mm: RegExpExecArray | null;
        while ((mm = idRe.exec(data.html)) !== null) newIds.push(mm[1]);
        props.onClearFixIds?.(newIds);
        setCardsHtml(prev => (prev ? `${prev}\n${data.html}` : data.html!));
        setStatus(`Done — review the opportunities below (grouped by target) and click ⚡ Approve to apply each link.`);
      } else {
        setStatus(`⚠ ${data.error ?? "Failed to generate internal links"}`);
      }
    } catch (e) {
      setStatus(`⚠ ${e instanceof Error ? e.message : "Network error"}`);
    } finally {
      setBuilding(false);
    }
  }

  return (
    <div className="content-drawer" style={{ marginTop: 24 }}>
      {/* Dedicated site-crawl toolbar — same top-right position as the Run Crawl button on other tabs. */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, padding: "0 2px", marginBottom: 16 }}>
        {crawlStatus && <span className="cd-status" style={{ marginRight: "auto" }}>{crawlStatus}</span>}
        {crawledAt && !crawlStatus && (
          <span style={{ fontSize: 10, color: "var(--ink4, rgba(0,0,0,.45))", fontFamily: "Roboto Mono, monospace", whiteSpace: "nowrap" }}>
            Last crawled: {(() => { const d = new Date(crawledAt); return isNaN(d.getTime()) ? crawledAt.slice(0, 10) : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); })()}
          </span>
        )}
        <span className="audit-tip">
          <button
            className={`run-crawl-btn${crawling ? " crawling" : ""}`}
            onClick={handleCrawlSite}
            disabled={crawling}
            style={{
              padding: "9px 22px", background: "linear-gradient(135deg, #ea580c, #f97316)", color: "#fff",
              border: "none", borderRadius: 0, fontSize: 13, fontWeight: 700, letterSpacing: ".3px",
              cursor: crawling ? "default" : "pointer", fontFamily: "'Inter', 'Mulish', sans-serif",
              whiteSpace: "nowrap", boxShadow: "0 2px 8px rgba(234,88,12,.4)",
            }}
          >
            {crawling ? "⏳ Crawling…" : "Crawl Website"}
          </button>
          <span className="audit-tip-box" style={{ left: "auto", right: 0, transform: "none", textTransform: "none", fontFamily: "'Inter','Mulish',sans-serif", fontWeight: 400, letterSpacing: "normal" }}>
            {"The crawl reads all pages on the website so the tool can determine internal-link opportunities. Run this first."}
          </span>
        </span>
      </div>
      <div className="cd-section">
        <div className="cd-section-hd">
          <span className="cd-hd-brand">Bulk Internal Links</span>
          <span className="audit-tip" style={{ marginLeft: 2 }}>
            <span className="audit-tip-icon">i</span>
            <span className="audit-tip-box" style={{ textTransform: "none", fontFamily: "'Inter','Mulish',sans-serif", fontWeight: 400, letterSpacing: "normal" }}>
              {"Paste the URLs of pages you want OTHER pages on your site to link to. First click Crawl Website (top of this tab) so the tool has your site's pages — this is a standalone tool and does NOT need competitors. On Continue, it finds relevant source pages by their page title, then scans those pages' content for natural link opportunities to each target — preferring existing anchor text, or adding a sentence where none fits. Any page that already links to a Target URL in its BODY text is excluded as a source for that target, to avoid duplicate links. A card is created per opportunity, grouped and ordered by target URL."}
            </span>
          </span>
        </div>
        <p className="cd-help">
          First click <strong>Crawl Website</strong> (top of this tab) to read your pages. Then paste one
          <strong> target URL per line</strong> (the pages you want linked to) and click <strong>Continue</strong>.
          Each opportunity gets a card — click <strong>⚡ Approve</strong> to insert the link in WordPress.
        </p>
        <div className="bil-maxrow">
          <label htmlFor="bil-maxper" className="bil-maxlabel">
            Maximum Number of Link Suggestions per URL
            <span className="audit-tip" style={{ marginLeft: 6 }}>
              <span className="audit-tip-icon">i</span>
              <span className="audit-tip-box" style={{ width: 500, textTransform: "none", fontFamily: "'Inter','Mulish',sans-serif", fontWeight: 400, letterSpacing: "normal" }}>
                {"Caps how many internal-link opportunities are suggested for EACH target URL. \"Max\" returns every relevant source page found for a URL, so you never miss an opportunity. The link opportunities below are produced after filtering unqualified link source candidates (an existing internal link to the target URL, or a page not relevant enough). While a maximum number of link suggestions can be chosen, the actual total number of link suggestions shown are dependent on the number of relevant opportunities that do not currently link to your chosen target URL. Therefore you might not reach your maximum number of link suggestions if the link opportunity pool is not large enough for that particular target URL."}
              </span>
            </span>
          </label>
          <select id="bil-maxper" className="bil-maxsel" value={maxPerUrl} onChange={e => setMaxPerUrl(e.target.value)}>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="30">30</option>
            <option value="max">Max</option>
          </select>
        </div>
        <textarea
          className="cd-titles"
          value={urls}
          onChange={e => setUrls(e.target.value)}
          rows={5}
          placeholder={"https://yoursite.com/services/teen-anxiety/\nhttps://yoursite.com/locations/montville/\nhttps://yoursite.com/programs/iop/"}
        />
        <div className="cd-actions">
          <button className="cd-continue-btn" onClick={handleContinue} disabled={building}>
            {building ? "Working…" : "Continue →"}
          </button>
          {status && <span className="cd-status">{status}</span>}
        </div>
      </div>

      {/* Generated opportunity cards — rendered through AuditSection (id="internal") so the
          ⚡ Approve wiring → execInternalLinkFix (WordPress) applies, same as the tab's cards. */}
      {cardsHtml && (
        <div className="cd-cards" ref={cardsRef}>
          <AuditSection
            id="internal"
            keepButtons
            html={cardsHtml}
            state={props.state}
            siteBrand={props.siteBrand}
            auditDate={props.auditDate}
            onApprove={props.onApprove}
            onIgnore={props.onIgnore}
            onDone={props.onDone}
            onReset={props.onReset}
            hidden={props.hidden}
            onToggleHide={props.onToggleHide}
            connected={props.connected}
            fetchPageTags={props.fetchPageTags}
            onOpenSchemaSetup={props.onOpenSchemaSetup}
          />
        </div>
      )}
    </div>
  );
}
